/**
 * Sovereign Deployment map — single source of truth.
 * Copy is British English, no contractions, no em dashes.
 *
 * category: "deployment" | "pursuit" | "delivery-experience"
 * emphasis: 1 | 2 | 3   → marker size tier
 * product:  "Foundation" | "Realloc" | "Cloud" | "Atom"
 */
export const LOCATIONS = [
  /* ---------------- Africa ---------------- */
  {
    id: "ng-lagos",
    country: "Nigeria",
    city: "Lagos",
    coordinates: [3.3792, 6.5244],
    category: "deployment",
    product: "Foundation",
    headline: "Sovereign deployment",
    description:
      "Foundation deployed for a Nigerian institution, running in-country under Nigerian regulator authority.",
    emphasis: 3,
  },
  {
    id: "gh-accra",
    country: "Ghana",
    city: "Accra",
    coordinates: [-0.187, 5.6037],
    category: "deployment",
    product: "Foundation",
    headline: "Sovereign deployment",
    description:
      "Foundation deployed for a Ghanaian institution, resident in-country.",
    emphasis: 2,
  },
  {
    id: "ke-nairobi",
    country: "Kenya",
    city: "Nairobi",
    coordinates: [36.8219, -1.2921],
    category: "deployment",
    product: "Realloc",
    headline: "Sovereign deployment",
    description:
      "Realloc live inside an East African institutional environment.",
    emphasis: 2,
  },
  {
    id: "za-johannesburg",
    country: "South Africa",
    city: "Johannesburg",
    coordinates: [28.0473, -26.2041],
    category: "deployment",
    product: "Cloud",
    headline: "Sovereign deployment",
    description:
      "Cloud deployed for a South African financial institution, in-country.",
    emphasis: 2,
  },
  {
    id: "eg-cairo",
    country: "Egypt",
    city: "Cairo",
    coordinates: [31.2357, 30.0444],
    category: "pursuit",
    product: "Foundation",
    headline: "Active pursuit",
    description:
      "Sovereign Foundation deployment under active pursuit with an Egyptian regulator-facing institution.",
    emphasis: 2,
  },

  /* ------------- Middle East ------------- */
  {
    id: "ae-dubai",
    country: "United Arab Emirates",
    city: "Dubai",
    coordinates: [55.2708, 25.2048],
    category: "deployment",
    product: "Realloc",
    headline: "Sovereign deployment",
    description:
      "Realloc deployed inside a UAE institution, under Emirates data residency.",
    emphasis: 2,
  },
  {
    id: "sa-riyadh",
    country: "Saudi Arabia",
    city: "Riyadh",
    coordinates: [46.6753, 24.7136],
    category: "pursuit",
    product: "Foundation",
    headline: "Active pursuit",
    description:
      "Foundation engagement in advanced pursuit with a Saudi institution.",
    emphasis: 1,
  },

  /* ---------------- Asia ----------------- */
  {
    id: "in-mumbai",
    country: "India",
    city: "Mumbai",
    coordinates: [72.8777, 19.076],
    category: "deployment",
    product: "Cloud",
    headline: "Sovereign deployment",
    description:
      "Cloud deployed for an Indian institutional customer, in-country.",
    emphasis: 2,
  },
  {
    id: "id-jakarta",
    country: "Indonesia",
    city: "Jakarta",
    coordinates: [106.8456, -6.2088],
    category: "deployment",
    product: "Atom",
    headline: "Sovereign deployment",
    description:
      "Atom deployed for a regulated Indonesian institution, on infrastructure the client controls.",
    emphasis: 1,
  },
  {
    id: "sg-singapore",
    country: "Singapore",
    city: "Singapore",
    coordinates: [103.8198, 1.3521],
    category: "deployment",
    product: "Foundation",
    headline: "Sovereign deployment",
    description:
      "Foundation deployed for a Singaporean institution, under MAS-aligned residency.",
    emphasis: 1,
  },

  /* ------------- Caribbean --------------- */
  {
    id: "jm-kingston",
    country: "Jamaica",
    city: "Kingston",
    coordinates: [-76.7936, 17.9714],
    category: "deployment",
    product: "Realloc",
    headline: "Sovereign deployment",
    description:
      "Realloc deployed for Sagicor operations, resident on ground the institution controls.",
    emphasis: 3,
  },
  {
    id: "bb-bridgetown",
    country: "Barbados",
    city: "Bridgetown",
    coordinates: [-59.6132, 13.1132],
    category: "deployment",
    product: "Realloc",
    headline: "Sovereign deployment",
    description:
      "Realloc in production across Sagicor Barbados, deployed inside the institution's environment.",
    emphasis: 2,
  },
  {
    id: "tt-portofspain",
    country: "Trinidad and Tobago",
    city: "Port of Spain",
    coordinates: [-61.5178, 10.6549],
    category: "deployment",
    product: "Realloc",
    headline: "Sovereign deployment",
    description:
      "Realloc deployed for Sagicor Trinidad and Tobago, running in-region and in-country.",
    emphasis: 2,
  },
  {
    id: "bs-nassau",
    country: "Bahamas",
    city: "Nassau",
    coordinates: [-77.3963, 25.0343],
    category: "pursuit",
    product: "Realloc",
    headline: "Active pursuit",
    description:
      "Sovereign deployment under active pursuit with a regulated Bahamian institution.",
    emphasis: 1,
  },

  /* ----------- Latin America ------------- */
  {
    id: "br-saopaulo",
    country: "Brazil",
    city: "São Paulo",
    coordinates: [-46.6333, -23.5505],
    category: "deployment",
    product: "Realloc",
    headline: "Sovereign deployment",
    description:
      "Realloc deployed for a Brazilian institutional customer, in-country.",
    emphasis: 2,
  },
  {
    id: "mx-mexicocity",
    country: "Mexico",
    city: "Mexico City",
    coordinates: [-99.1332, 19.4326],
    category: "deployment",
    product: "Cloud",
    headline: "Sovereign deployment",
    description:
      "Cloud live inside a Mexican institution, resident in-country.",
    emphasis: 1,
  },

  /* ---------- North America & UK --------- */
  {
    id: "us-newyork",
    country: "United States",
    city: "New York",
    coordinates: [-74.006, 40.7128],
    category: "delivery-experience",
    product: "Atom",
    headline: "Delivery experience",
    description:
      "United States operations served under the Atom programme.",
    emphasis: 1,
  },
  {
    id: "ca-toronto",
    country: "Canada",
    city: "Toronto",
    coordinates: [-79.3832, 43.6532],
    category: "delivery-experience",
    product: "Realloc",
    headline: "Delivery experience",
    description:
      "Sagicor North American operations reached through the Realloc programme.",
    emphasis: 1,
  },
  {
    id: "uk-london",
    country: "United Kingdom",
    city: "London",
    coordinates: [-0.1276, 51.5074],
    category: "delivery-experience",
    product: "Foundation",
    headline: "Delivery experience",
    description:
      "Programme leadership and delivery experience seated in London.",
    emphasis: 1,
  },
];

/** Great-circle arcs between hub locations (Phase 2). */
export const ARCS = [
  { from: "ng-lagos", to: "gh-accra" },
  { from: "ng-lagos", to: "jm-kingston" },
  { from: "ng-lagos", to: "za-johannesburg" },
  { from: "ke-nairobi", to: "ae-dubai" },
  { from: "ae-dubai", to: "in-mumbai" },
  { from: "in-mumbai", to: "sg-singapore" },
  { from: "sg-singapore", to: "id-jakarta" },
  { from: "jm-kingston", to: "bb-bridgetown" },
  { from: "jm-kingston", to: "tt-portofspain" },
  { from: "jm-kingston", to: "bs-nassau" },
  { from: "br-saopaulo", to: "mx-mexicocity" },
];

/** Region framings — used in Phase 3. */
export const REGIONS = {
  all: { center: [15, 15], scale: 160 },
  africa: { center: [22, 5], scale: 380 },
  caribbean: { center: [-70, 18], scale: 620 },
  "middle-east": { center: [45, 27], scale: 480 },
  asia: { center: [95, 15], scale: 340 },
  "latin-america": { center: [-70, -10], scale: 300 },
};

/** Legend entries. */
export const LEGEND = [
  { key: "deployment", label: "Sovereign deployment" },
  { key: "pursuit", label: "Active pursuit" },
  { key: "delivery-experience", label: "Delivery experience" },
];
